import * as Sentry from '@sentry/vue'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'
import ANNOTATION_TYPES from '@/constant/annotationTypes'
import SlideAnnotationsApi from '@/api/slideAnnotations'
import { syncSlidesWithAnnotations } from '@/business-logic/slide-annotations'
import util from '@/util'

const _state = () => ({
  annotationsBySlide: {}, // { [slideId]: annotationObj }
  firstAnnotatedSlideId: null, // First slide ID with annotation from socket events
})

const _mutations = {
  SET_ANNOTATIONS(state, { annotations }) {
    const map = {}
    annotations.forEach((a) => {
      if (a?.slideId != null) {
        map[a.slideId] = a
      }
    })
    state.annotationsBySlide = map
  },
  SET_FIRST_ANNOTATED_SLIDE_ID(state, slideId) {
    state.firstAnnotatedSlideId = slideId
  },
  CLEAR_FIRST_ANNOTATED_SLIDE_ID(state) {
    state.firstAnnotatedSlideId = null
  },
  CLEAR_ANNOTATION(state, slideId) {
    if (state.annotationsBySlide[slideId]) {
      // Remove the annotation entirely from the state
      delete state.annotationsBySlide[slideId]
    }
  },
}

const _actions = {
  async fetchAnnotationsAction({ commit, rootState }) {
    try {
      if (!rootState.presentation || !rootState.presentation.id) return

      const presentationId = rootState.presentation.id
      const annotations = await SlideAnnotationsApi.getSlideAnnotationsByPresentationId(
        presentationId,
      )

      await commit('SET_ANNOTATIONS', {
        annotations,
      })
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  async clearAnnotationAction({ commit, dispatch }, slideId) {
    try {
      await SlideAnnotationsApi.updateAnnotationVisibility(slideId)

      commit('CLEAR_ANNOTATION', slideId)

      dispatch('updateSlideByIdAction', {
        id: slideId,
        annotationIsVisible: false,
      }, { root: true })
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  async syncAnnotationsAction({ state, dispatch, rootState }) {
    try {
      if (!rootState.presentation?.slides) return

      const originalSlides = rootState.presentation.slides
      const annotations = state.annotationsBySlide

      // Create a new slides array with updated annotation data
      const { updatedSlides, hasChanges } = syncSlidesWithAnnotations(originalSlides, annotations)

      // Only dispatch if changes were made
      if (hasChanges) {
        dispatch('updatePresentationAction', { slides: updatedSlides }, { root: true })
      }
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  async safelyHandleAnnotationSyncAction({ dispatch, rootState, state }) {
    try {
      // Check if presentation and slides are ready, or if we need to sync annotations for slides not yet in the presentation
      const slides = rootState.presentation?.slides
      const shouldRetry = !slides ||
        // Check if there are annotations for slides that don't exist in the presentation yet
        Object.keys(state.annotationsBySlide)
          .some((slideId) => !slides.find((slide) => slide?.id?.toString() === slideId?.toString()))

      if (shouldRetry) {
        await util.sleep(300)
        dispatch('safelyHandleAnnotationSyncAction')
        return
      }

      // Call the regular sync action once we have slides
      dispatch('syncAnnotationsAction')
    } catch (error) {
      Sentry.captureException(error)
    }
  },
  setFirstAnnotatedSlideId({ commit }, slideId) {
    commit('SET_FIRST_ANNOTATED_SLIDE_ID', slideId)
  },
  clearFirstAnnotatedSlideId({ commit }) {
    commit('CLEAR_FIRST_ANNOTATED_SLIDE_ID')
  },
}

const _getters = {
  getAnnotationBySlideId: (state) => (slideId) => state.annotationsBySlide[slideId] || null,

  canUseSlideAnnotations: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-slide-annotations')
  },

  isAIAnnotationVisible: (_, __, ___, rootGetters) => (slide) => {
    return slide.annotationIsVisible &&
           slide.annotationType === ANNOTATION_TYPES.AI &&
           isFeatureEnabled(rootGetters, 'release-slide-annotations')
  },

  annotationsBySlide: (state) => state.annotationsBySlide,

  firstAnnotatedSlideId: (state) => state.firstAnnotatedSlideId,
}

export default {
  namespaced: true,
  state: _state,
  mutations: _mutations,
  actions: _actions,
  getters: _getters,
}
